import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import MessageBubble from './MessageBubble';
import ProposalCard from './ProposalCard';
import FileUploadModal from './FileUploadModal';
import ProposalModal from './ProposalModal';

const ChatInterface = ({ 
  conversation, 
  messages, 
  onSendMessage, 
  onSendProposal,
  currentUserId, 
  sending,
  searchQuery,
  onSearchChange,
  onToggleDetails
}) => {
  const [messageInput, setMessageInput] = useState('');
  const [showFileModal, setShowFileModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (e) => {
    e?.preventDefault();
    if (messageInput?.trim() && !sending) {
      onSendMessage(messageInput);
      setMessageInput('');
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setShowFileModal(true);
    }
  };

  const handleFileUpload = (file, caption) => {
    onSendMessage(caption, file);
    setShowFileModal(false);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProposalSend = (proposalData) => {
    onSendProposal(proposalData);
    setShowProposalModal(false);
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
            <Image 
              src={conversation?.otherParticipant?.avatar_url || '/assets/images/no_image.png'} 
              alt={`${conversation?.otherParticipant?.full_name || 'User'} avatar`}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {conversation?.otherParticipant?.full_name || 'Unknown User'}
            </h3>
            <p className="text-xs text-muted-foreground capitalize">
              {conversation?.otherParticipant?.role || 'User'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSearch(!showSearch)}
            title="Search messages"
          >
            <Icon name="Search" size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleDetails}
            title="Conversation details"
          >
            <Icon name="Info" size={20} />
          </Button>
        </div>
      </div>
      {/* Search Bar */}
      {showSearch && (
        <div className="p-3 border-b border-border bg-muted/30">
          <div className="relative">
            <Icon 
              name="Search" 
              size={16} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <input
              type="text"
              placeholder="Search in conversation..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
      )}
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div>
              <Icon name="MessageCircle" size={48} className="mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages?.map((message) => (
            <div key={message?.id}>
              {message?.is_proposal ? (
                <ProposalCard 
                  proposal={message?.proposal_data}
                  messageId={message?.id}
                  isSender={message?.sender_id === currentUserId}
                  timestamp={message?.created_at}
                />
              ) : (
                <MessageBubble
                  message={message}
                  isSender={message?.sender_id === currentUserId}
                  currentUserId={currentUserId}
                />
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input Area */}
      <form onSubmit={handleSend} className="p-4 border-t border-border">
        <div className="flex items-end gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,video/*,audio/*,.pdf"
          />
          
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef?.current?.click()}
            title="Attach file"
          >
            <Icon name="Paperclip" size={20} />
          </Button>
          
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowProposalModal(true)}
            title="Send proposal"
          >
            <Icon name="FileText" size={20} />
          </Button>
          
          <textarea
            value={messageInput}
            onChange={(e) => setMessageInput(e?.target?.value)}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' && !e?.shiftKey) {
                e?.preventDefault();
                handleSend(e);
              }
            }}
            placeholder="Type your message..."
            rows={1}
            className="flex-1 px-4 py-2 bg-muted border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          
          <Button
            type="submit"
            disabled={!messageInput?.trim() || sending}
            iconName="Send"
            title="Send message"
          >
            Send
          </Button>
        </div>
      </form>
      {/* Modals */}
      {showFileModal && fileInputRef?.current?.files?.[0] && (
        <FileUploadModal
          file={fileInputRef?.current?.files?.[0]}
          onUpload={handleFileUpload}
          onClose={() => {
            setShowFileModal(false);
            if (fileInputRef?.current) {
              fileInputRef.current.value = '';
            }
          }}
        />
      )}
      {showProposalModal && (
        <ProposalModal
          onSend={handleProposalSend}
          onClose={() => setShowProposalModal(false)}
        />
      )}
    </>
  );
};

export default ChatInterface;